import { Box, Button, Paper, Step, StepLabel, Stepper, Typography, useTheme } from '@mui/material'
import React, { useState } from 'react'
import { ValutTypeStep } from './Steps/VaultTypeStep'
import { VaultParamsStep } from './Steps/VaultParamsStep'
import { PoolTypeEnum, RoleEnum } from '@shared/Enums'
import { EthAddr } from '@shared/SchemaTypes'
import AdminGrantPoolAccessPage from '@react/admin/AdminGrantPoolAccessPage'
import { VaultFilesStep } from './Steps/VaultFilesStep'
import { PoolFlexCalendar } from '@react/components/pools/PoolFlexCalendat'
import { useNavigate } from 'react-router-dom'
import { ActivateVaultStep } from './Steps/ActivateVaultStep'

interface VaultStep {
  label: string
  component: () => React.ReactElement
  handleComplete?: (addr: EthAddr) => void
  showNext?: boolean
  showBack?: boolean
}

export const PoolCreateMultiStepForm = () => {
  const [activeStep, setActiveStep] = useState(0)
  const [formState, setFormState] = useState<any>({
    vaultType: '',
  })

  const [currentPoolAddr, setCurrentPoolAddr] = useState<string>()
  const [nextEnabled, setNextEnabled] = useState(false)

  const navigate = useNavigate()
  const theme = useTheme()

  const handleStepChange = (field: string, value: any) => {
    setFormState({ ...formState, [field]: value })
  }

  const onFinish = () => {
    navigate(`/pools/${currentPoolAddr}`)
  }

  const handleNext = () => {
    if (formState.vaultType === PoolTypeEnum.Term && activeStep === steps.length - 1) {
      onFinish()
    }
    if (steps.length > activeStep + 1 && steps[activeStep + 1].handleComplete !== undefined) setNextEnabled(false)
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleReset = () => {
    setActiveStep(0)
  }

  const steps: VaultStep[] = [
    {
      label: 'Vault type',
      component: () => (
        <ValutTypeStep
          vaultType={formState.vaultType}
          handleChange={(val) => {
            handleStepChange('vaultType', val.target.value)
            setNextEnabled(true)
          }}
        />
      ),
    },
    {
      label: 'Vault parameters',
      showNext: false,
      component: () => (
        <VaultParamsStep
          vaultType={formState.vaultType}
          vaultParams={formState.vaultParams}
          handleChange={(val) => handleStepChange('params', val)}
          handleComplete={(addr) => {
            setCurrentPoolAddr(addr)
            handleNext()
          }}
        />
      ),
    },
    {
      label: 'Icons and Files',
      component: () => (
        <>
          <VaultFilesStep poolAddr={currentPoolAddr as string} onComplete={() => setNextEnabled(true)} />
        </>
      ),
    },
    {
      label: 'Access List',
      component: () => (
        <AdminGrantPoolAccessPage
          poolAddr={currentPoolAddr as string}
          setHasSomeGranted={(v) => {
            if (v === true) {
              setNextEnabled(true)
            }
          }}
        />
      ),
    },
    {
      label: 'Activate Vault',
      component: () => (
        <ActivateVaultStep
          poolAddr={currentPoolAddr as string}
          handleComplete={() => {
            handleNext()
          }}
        />
      ),
    },
  ]

  if (formState.vaultType === PoolTypeEnum.FlexibleRate) {
    steps.push({
      label: 'Set Dates',
      component: () => (
        <Paper sx={{ padding: 20 }}>
          <PoolFlexCalendar role={RoleEnum.POOL_ADMIN} startInUpdate poolAddr={currentPoolAddr as string} />
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: 4 }}>
            <Button
              onClick={() => {
                onFinish()
              }}
              variant={'contained'}
              sx={{
                backgroundColor: theme.palette.fg.main,
                color: '#FFFFFF',
                padding: '11px 16px 11px 16px',
                marginLeft: '8px',
                borderRadius: '6px',
              }}
            >
              <Typography variant={'buttonBold'}>Complete</Typography>
            </Button>
          </Box>
        </Paper>
      ),
      showNext: false,
      showBack: false,
    })
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography sx={{ textAlign: 'left', marginBottom: 12 }} variant="h2">
        Create a new Loan Vault
      </Typography>

      <Stepper activeStep={activeStep}>
        {steps.map((step, _index) => {
          const stepProps: { completed?: boolean } = {}
          const labelProps: {
            optional?: React.ReactNode
          } = {}
          return (
            <Step key={step.label} {...stepProps}>
              <StepLabel {...labelProps}>{step.label}</StepLabel>
            </Step>
          )
        })}
      </Stepper>
      {activeStep === steps.length ? (
        <React.Fragment>
          <Typography sx={{ mt: 2, mb: 1 }}>All steps completed - create vault submit...</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
            <Box sx={{ flex: '1 1 auto' }} />
            <Button onClick={handleReset}>Reset</Button>
          </Box>
        </React.Fragment>
      ) : (
        <Box sx={{ marginTop: 20 }}>
          {steps[activeStep].component !== null && steps[activeStep].component !== undefined && (
            <>{steps[activeStep].component()}</>
          )}
          {steps[activeStep].component == null && <>{<Typography>No step component</Typography>}</>}
          <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
            {steps[activeStep].showBack === false ? (
              <></>
            ) : (
              <Button color="inherit" disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
                Back
              </Button>
            )}
            <Box sx={{ flex: '1 1 auto' }} />
            {steps[activeStep].showNext === false ? (
              <></>
            ) : (
              <Button disabled={!nextEnabled} onClick={handleNext}>
                {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}
